import React, { useState } from "react";

// ============================================
// ICONS
// ============================================

function IconX({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function IconMapPin({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
    </svg>
  );
}

function IconChevronDown({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// ============================================
// REGIONAL PLAN DATA
// ============================================

interface RegionalPlan {
  value: string;
  label: string;
  states: string[];
  criteriaSystem?: "interqual" | "mcg" | "mixed" | "proprietary";
}

// ============================================
// TRICARE REGIONAL PLANS
// ============================================

interface TricareRegion {
  value: string;
  label: string;
  states: string[];
  contractor: string;
}

const TRICARE_REGIONAL_PLANS: TricareRegion[] = [
  { 
    value: "tricare_east", 
    label: "Tricare East", 
    states: ["AL", "CT", "DE", "DC", "FL", "GA", "IL", "IN", "KY", "ME", "MD", "MA", "MI", "NH", "NJ", "NY", "NC", "OH", "PA", "RI", "SC", "TN", "VT", "VA", "WV", "WI"],
    contractor: "Humana Military"
  },
  { 
    value: "tricare_west", 
    label: "Tricare West", 
    states: ["AK", "AZ", "CA", "CO", "HI", "ID", "IA", "KS", "LA", "MN", "MO", "MT", "NE", "NV", "NM", "ND", "OK", "OR", "SD", "TX", "UT", "WA", "WY"],
    contractor: "Health Net Federal Services"
  },
  { 
    value: "tricare_overseas", 
    label: "Tricare Overseas", 
    states: [],
    contractor: "International SOS"
  },
];

// ============================================
// KAISER PERMANENTE REGIONAL PLANS
// ============================================

interface KaiserRegion {
  value: string;
  label: string;
  states: string[];
}

const KAISER_REGIONAL_PLANS: KaiserRegion[] = [
  { value: "kaiser_norcal", label: "Kaiser Permanente Northern California", states: ["CA"] },
  { value: "kaiser_socal", label: "Kaiser Permanente Southern California", states: ["CA"] },
  { value: "kaiser_colorado", label: "Kaiser Permanente Colorado", states: ["CO"] },
  { value: "kaiser_georgia", label: "Kaiser Permanente Georgia", states: ["GA"] },
  { value: "kaiser_hawaii", label: "Kaiser Permanente Hawaii", states: ["HI"] },
  { value: "kaiser_midatlantic", label: "Kaiser Permanente Mid-Atlantic", states: ["MD", "VA", "DC"] },
  { value: "kaiser_northwest", label: "Kaiser Permanente Northwest", states: ["OR", "WA"] },
  { value: "kaiser_washington", label: "Kaiser Permanente Washington", states: ["WA"] },
];

// ============================================
// MEDICAID STATE PLANS
// ============================================

interface MedicaidPlan {
  value: string;
  label: string;
  state: string;
  programName: string;
  majorMCOs?: string[];
  notes?: string;
}

const MEDICAID_STATE_PLANS: MedicaidPlan[] = [
  { value: "medicaid_al", label: "Alabama Medicaid", state: "AL", programName: "Alabama Medicaid", majorMCOs: ["Alabama Coordinated Health Network"], notes: "Fee-for-service dominant" },
  { value: "medicaid_ak", label: "Alaska Medicaid", state: "AK", programName: "Denali KidCare / Alaska Medicaid", notes: "Fee-for-service, tribal health integration" },
  { value: "medicaid_az", label: "Arizona AHCCCS", state: "AZ", programName: "AHCCCS", majorMCOs: ["AHCCCS Complete Care (ACC)", "Banner-University", "Arizona Complete Health", "Care1st", "Health Choice Arizona", "Mercy Care", "Molina", "UnitedHealthcare"] },
  { value: "medicaid_ar", label: "Arkansas Medicaid", state: "AR", programName: "ARKids / Arkansas Medicaid", majorMCOs: ["Arkansas Total Care (Centene)", "Empower Healthcare Solutions"], notes: "Provider-led model" },
  { value: "medicaid_ca", label: "California Medi-Cal", state: "CA", programName: "Medi-Cal", majorMCOs: ["Anthem Blue Cross", "Blue Shield of CA Promise", "CalOptima", "Health Net", "Health Plan of San Mateo", "Inland Empire Health Plan", "Kaiser", "L.A. Care", "Molina", "Partnership HealthPlan"], notes: "County-organized, largest Medicaid program" },
  { value: "medicaid_co", label: "Colorado Health First", state: "CO", programName: "Health First Colorado", majorMCOs: ["Colorado Access", "Denver Health", "Rocky Mountain Health Plans", "Kaiser"], notes: "Regional organization model" },
  { value: "medicaid_ct", label: "Connecticut HUSKY", state: "CT", programName: "HUSKY Health", majorMCOs: ["Community Health Network of CT", "Connecticut Behavioral Health Partnership"] },
  { value: "medicaid_de", label: "Delaware Medicaid", state: "DE", programName: "Diamond State Health Plan", majorMCOs: ["AmeriHealth Caritas", "Highmark Health Options"] },
  { value: "medicaid_dc", label: "DC Medicaid", state: "DC", programName: "DC Healthy Families", majorMCOs: ["AmeriHealth Caritas DC", "CareFirst Community Health Plan", "MedStar Family Choice"] },
  { value: "medicaid_fl", label: "Florida Medicaid", state: "FL", programName: "Florida Medicaid / MMA", majorMCOs: ["Aetna Better Health", "AmeriHealth Caritas", "Community Care Plan", "Humana", "Molina", "Simply Healthcare (Anthem)", "Sunshine Health (Centene)", "UnitedHealthcare"] },
  { value: "medicaid_ga", label: "Georgia Medicaid", state: "GA", programName: "Georgia Families", majorMCOs: ["Amerigroup", "CareSource", "Peach State Health Plan (Centene)"] },
  { value: "medicaid_hi", label: "Hawaii QUEST", state: "HI", programName: "Med-QUEST", majorMCOs: ["AlohaCare", "HMSA", "Kaiser", "Ohana Health Plan (WellCare)", "UnitedHealthcare"] },
  { value: "medicaid_id", label: "Idaho Medicaid", state: "ID", programName: "Idaho Medicaid", majorMCOs: ["Blue Cross of Idaho", "Molina", "Regence"], notes: "Managed care expansion recent" },
  { value: "medicaid_il", label: "Illinois Medicaid", state: "IL", programName: "Illinois Medicaid", majorMCOs: ["Aetna Better Health", "Blue Cross Blue Shield of IL", "CountyCare", "Meridian (Centene)", "Molina", "YouthCare"] },
  { value: "medicaid_in", label: "Indiana Medicaid", state: "IN", programName: "Hoosier Healthwise / HIP", majorMCOs: ["Anthem", "CareSource", "MDwise", "Managed Health Services (Centene)", "UnitedHealthcare"] },
  { value: "medicaid_ia", label: "Iowa Medicaid", state: "IA", programName: "Iowa Health Link", majorMCOs: ["Amerigroup", "Iowa Total Care (Centene)", "Molina"] },
  { value: "medicaid_ks", label: "Kansas KanCare", state: "KS", programName: "KanCare", majorMCOs: ["Aetna Better Health", "Sunflower Health Plan (Centene)", "UnitedHealthcare"] },
  { value: "medicaid_ky", label: "Kentucky Medicaid", state: "KY", programName: "Kentucky Medicaid", majorMCOs: ["Aetna Better Health", "Anthem", "Humana CareSource", "Passport Health Plan", "WellCare"] },
  { value: "medicaid_la", label: "Louisiana Medicaid", state: "LA", programName: "Healthy Louisiana", majorMCOs: ["Aetna Better Health", "AmeriHealth Caritas", "Healthy Blue (Anthem)", "Louisiana Healthcare Connections (Centene)", "UnitedHealthcare"] },
  { value: "medicaid_me", label: "Maine MaineCare", state: "ME", programName: "MaineCare", notes: "Primarily fee-for-service with Primary Care Case Management" },
  { value: "medicaid_md", label: "Maryland HealthChoice", state: "MD", programName: "HealthChoice", majorMCOs: ["Aetna Better Health", "Amerigroup", "CareFirst Community Health Plan", "Jai Medical Systems", "Kaiser", "Maryland Physicians Care", "MedStar Family Choice", "Molina", "Priority Partners", "UnitedHealthcare"] },
  { value: "medicaid_ma", label: "Massachusetts MassHealth", state: "MA", programName: "MassHealth", majorMCOs: ["AllWays Health Partners", "BMC HealthNet Plan", "Fallon Health", "Health New England", "Tufts Health Plan"], notes: "ACO-based model" },
  { value: "medicaid_mi", label: "Michigan Medicaid", state: "MI", programName: "Michigan Medicaid", majorMCOs: ["Aetna Better Health", "Blue Cross Complete", "HAP Midwest", "McLaren Health Plan", "Meridian (Centene)", "Molina", "Priority Health", "UnitedHealthcare"] },
  { value: "medicaid_mn", label: "Minnesota Medical Assistance", state: "MN", programName: "Medical Assistance / MinnesotaCare", majorMCOs: ["Blue Plus", "HealthPartners", "Hennepin Health", "Medica", "PrimeWest", "South Country Health Alliance", "UCare"] },
  { value: "medicaid_ms", label: "Mississippi Medicaid", state: "MS", programName: "MississippiCAN", majorMCOs: ["Magnolia Health (Centene)", "Molina", "UnitedHealthcare"] },
  { value: "medicaid_mo", label: "Missouri MO HealthNet", state: "MO", programName: "MO HealthNet", majorMCOs: ["Aetna Better Health", "Home State Health (Centene)", "Healthy Blue (Anthem)", "UnitedHealthcare"] },
  { value: "medicaid_mt", label: "Montana Medicaid", state: "MT", programName: "Montana Medicaid", notes: "Fee-for-service with HELP program expansion" },
  { value: "medicaid_ne", label: "Nebraska Heritage Health", state: "NE", programName: "Heritage Health", majorMCOs: ["Healthy Blue (Anthem)", "Molina", "UnitedHealthcare"] },
  { value: "medicaid_nv", label: "Nevada Medicaid", state: "NV", programName: "Nevada Medicaid", majorMCOs: ["Anthem Blue Cross Blue Shield", "Health Plan of Nevada (UHC)", "Molina", "SilverSummit (Centene)"] },
  { value: "medicaid_nh", label: "New Hampshire Medicaid", state: "NH", programName: "NH Medicaid Care Management", majorMCOs: ["AmeriHealth Caritas", "Well Sense Health Plan"] },
  { value: "medicaid_nj", label: "New Jersey FamilyCare", state: "NJ", programName: "NJ FamilyCare", majorMCOs: ["Aetna Better Health", "Amerigroup", "Horizon NJ Health", "Molina", "UnitedHealthcare", "WellCare"] },
  { value: "medicaid_nm", label: "New Mexico Centennial Care", state: "NM", programName: "Centennial Care", majorMCOs: ["Blue Cross Blue Shield of NM", "Presbyterian Health Plan", "Western Sky Community Care (Centene)"] },
  { value: "medicaid_ny", label: "New York Medicaid", state: "NY", programName: "NY Medicaid Managed Care", majorMCOs: ["Affinity Health Plan", "Amerigroup", "Empire BCBS HealthPlus", "Fidelis Care", "Healthfirst", "MetroPlus", "Molina", "MVP Health Care", "UnitedHealthcare", "WellCare"], notes: "Largest managed care enrollment" },
  { value: "medicaid_nc", label: "North Carolina Medicaid", state: "NC", programName: "NC Medicaid Managed Care", majorMCOs: ["AmeriHealth Caritas", "Carolina Complete Health (Centene)", "Healthy Blue (Anthem)", "UnitedHealthcare", "WellCare"], notes: "Transitioned to managed care 2021" },
  { value: "medicaid_nd", label: "North Dakota Medicaid", state: "ND", programName: "ND Medicaid Expansion", majorMCOs: ["Sanford Health Plan"], notes: "Limited managed care" },
  { value: "medicaid_oh", label: "Ohio Medicaid", state: "OH", programName: "Ohio Medicaid", majorMCOs: ["Aetna Better Health", "AmeriHealth Caritas", "Buckeye Health Plan (Centene)", "CareSource", "Molina", "Paramount Advantage", "UnitedHealthcare"] },
  { value: "medicaid_ok", label: "Oklahoma SoonerCare", state: "OK", programName: "SoonerCare", majorMCOs: ["Aetna Better Health", "Humana Healthy Horizons", "Oklahoma Complete Health (Centene)", "UnitedHealthcare"], notes: "Transitioned to managed care 2024" },
  { value: "medicaid_or", label: "Oregon Health Plan", state: "OR", programName: "Oregon Health Plan (OHP)", majorMCOs: ["AllCare Health", "CareOregon", "Columbia Pacific CCO", "Health Share of Oregon", "Intercommunity Health Network", "Jackson Care Connect", "PacificSource", "Trillium Community Health", "Umpqua Health Alliance", "Yamhill CCO"], notes: "Coordinated Care Organization model" },
  { value: "medicaid_pa", label: "Pennsylvania Medicaid", state: "PA", programName: "PA HealthChoices", majorMCOs: ["AmeriHealth Caritas", "Geisinger Health Plan", "Gateway Health", "Health Partners Plans", "Highmark Wholecare", "UPMC Health Plan"] },
  { value: "medicaid_ri", label: "Rhode Island RIte Care", state: "RI", programName: "RIte Care", majorMCOs: ["Neighborhood Health Plan of RI", "Tufts Health Plan", "UnitedHealthcare"] },
  { value: "medicaid_sc", label: "South Carolina Healthy Connections", state: "SC", programName: "Healthy Connections", majorMCOs: ["Absolute Total Care (Centene)", "Healthy Blue (Anthem)", "Molina", "Select Health of SC (AmeriHealth)"] },
  { value: "medicaid_sd", label: "South Dakota Medicaid", state: "SD", programName: "SD Medicaid", notes: "Fee-for-service, limited managed care" },
  { value: "medicaid_tn", label: "Tennessee TennCare", state: "TN", programName: "TennCare", majorMCOs: ["Amerigroup", "BlueCare (BCBS TN)", "UnitedHealthcare"] },
  { value: "medicaid_tx", label: "Texas Medicaid", state: "TX", programName: "Texas Medicaid / STAR", majorMCOs: ["Aetna Better Health", "Amerigroup", "Blue Cross Blue Shield of TX", "Community Health Choice", "Cook Children's Health Plan", "Dell Children's Health Plan", "Driscoll Health Plan", "El Paso Health", "FirstCare Health Plans", "Molina", "Parkland Community Health Plan", "Superior HealthPlan (Centene)", "Texas Children's Health Plan", "UnitedHealthcare"], notes: "Regional service areas" },
  { value: "medicaid_ut", label: "Utah Medicaid", state: "UT", programName: "Utah Medicaid", majorMCOs: ["Healthy U (University of Utah)", "Molina", "SelectHealth"] },
  { value: "medicaid_vt", label: "Vermont Medicaid", state: "VT", programName: "Green Mountain Care", notes: "Single-payer-like, no MCOs" },
  { value: "medicaid_va", label: "Virginia Medicaid", state: "VA", programName: "Virginia Medicaid / Cardinal Care", majorMCOs: ["Aetna Better Health", "Anthem HealthKeepers", "Molina", "Optima Health", "UnitedHealthcare", "Virginia Premier"] },
  { value: "medicaid_wa", label: "Washington Apple Health", state: "WA", programName: "Apple Health", majorMCOs: ["Amerigroup", "Community Health Plan of WA", "Coordinated Care", "Molina", "UnitedHealthcare"] },
  { value: "medicaid_wv", label: "West Virginia Medicaid", state: "WV", programName: "WV Mountain Health Trust", majorMCOs: ["Aetna Better Health", "The Health Plan", "UniCare (Anthem)"] },
  { value: "medicaid_wi", label: "Wisconsin BadgerCare Plus", state: "WI", programName: "BadgerCare Plus", majorMCOs: ["Anthem Blue Cross Blue Shield", "Care Wisconsin Health Plan", "Children's Community Health Plan", "Dean Health Plan", "Group Health Cooperative", "MercyCare", "Molina", "Network Health Plan", "Physicians Plus", "Quartz", "Security Health Plan", "UnitedHealthcare"] },
  { value: "medicaid_wy", label: "Wyoming Medicaid", state: "WY", programName: "WY Medicaid", notes: "Fee-for-service, no managed care" },
];

// ============================================
// BCBS REGIONAL PLANS
// ============================================

// BCBS Regional Plans with their primary states and criteria systems
const BCBS_REGIONAL_PLANS: RegionalPlan[] = [
  { value: "anthem", label: "Anthem Blue Cross Blue Shield", states: ["CA", "CO", "CT", "GA", "IN", "KY", "ME", "MO", "NH", "NV", "NY", "OH", "VA", "WI"], criteriaSystem: "interqual" },
  { value: "bcbs_al", label: "BCBS of Alabama", states: ["AL"], criteriaSystem: "interqual" },
  { value: "bcbs_az", label: "BCBS of Arizona", states: ["AZ"], criteriaSystem: "interqual" },
  { value: "bcbs_fl", label: "Florida Blue (BCBS FL)", states: ["FL"], criteriaSystem: "interqual" },
  { value: "bcbs_ga", label: "BCBS of Georgia", states: ["GA"], criteriaSystem: "interqual" },
  { value: "bcbs_il", label: "BCBS of Illinois", states: ["IL"], criteriaSystem: "interqual" },
  { value: "bcbs_ma", label: "BCBS of Massachusetts", states: ["MA"], criteriaSystem: "interqual" },
  { value: "bcbs_mi", label: "BCBS of Michigan", states: ["MI"], criteriaSystem: "interqual" },
  { value: "bcbs_mn", label: "BCBS of Minnesota", states: ["MN"], criteriaSystem: "interqual" },
  { value: "bcbs_nc", label: "BCBS of North Carolina", states: ["NC"], criteriaSystem: "interqual" },
  { value: "bcbs_tx", label: "BCBS of Texas", states: ["TX"], criteriaSystem: "interqual" },
  { value: "empire_bcbs", label: "Empire BCBS (NY)", states: ["NY"], criteriaSystem: "interqual" },
  { value: "highmark", label: "Highmark BCBS", states: ["PA", "WV", "DE"], criteriaSystem: "mcg" },
  { value: "independence", label: "Independence Blue Cross", states: ["PA"], criteriaSystem: "interqual" },
  { value: "horizon", label: "Horizon BCBS of NJ", states: ["NJ"], criteriaSystem: "interqual" },
  { value: "carefirst", label: "CareFirst BCBS", states: ["MD", "DC", "VA"], criteriaSystem: "interqual" },
  { value: "premera", label: "Premera Blue Cross", states: ["WA", "AK"], criteriaSystem: "interqual" },
  { value: "regence", label: "Regence BCBS", states: ["OR", "UT", "ID", "WA"], criteriaSystem: "interqual" },
  { value: "bcbs_ks", label: "BCBS of Kansas", states: ["KS"], criteriaSystem: "interqual" },
  { value: "bcbs_la", label: "BCBS of Louisiana", states: ["LA"], criteriaSystem: "interqual" },
  { value: "bcbs_ne", label: "BCBS of Nebraska", states: ["NE"], criteriaSystem: "interqual" },
  { value: "bcbs_nm", label: "BCBS of New Mexico", states: ["NM"], criteriaSystem: "interqual" },
  { value: "bcbs_ok", label: "BCBS of Oklahoma", states: ["OK"], criteriaSystem: "interqual" },
  { value: "bcbs_sc", label: "BCBS of South Carolina", states: ["SC"], criteriaSystem: "interqual" },
  { value: "bcbs_tn", label: "BCBS of Tennessee", states: ["TN"], criteriaSystem: "interqual" },
  { value: "blue_shield_ca", label: "Blue Shield of California", states: ["CA"], criteriaSystem: "mcg" },
];

// Insurers that have regional variations requiring state selection
export const INSURERS_WITH_REGIONAL_PLANS: Record<string, RegionalPlan[]> = {
  "bcbs": BCBS_REGIONAL_PLANS,
  "kaiser": KAISER_REGIONAL_PLANS as RegionalPlan[],
  "tricare": TRICARE_REGIONAL_PLANS as unknown as RegionalPlan[],
};

// Medicaid plans indexed by state
export const MEDICAID_PLANS_BY_STATE: Record<string, MedicaidPlan> = Object.fromEntries(
  MEDICAID_STATE_PLANS.map(plan => [plan.state, plan])
);

// List of insurers that trigger state selection
export const REGIONAL_INSURERS = ["bcbs", "medicaid", "kaiser", "tricare"];

// State abbreviations for dropdown
const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "Washington DC" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

// ============================================
// COMPONENT PROPS
// ============================================

interface RegionalPlanSelectorProps {
  parentInsurer: string; // e.g., "bcbs"
  onSelect: (regionalPlanValue: string, regionalPlanLabel: string) => void;
  onCancel: () => void;
}

// ============================================
// COMPONENT
// ============================================

export function RegionalPlanSelector({ parentInsurer, onSelect, onCancel }: RegionalPlanSelectorProps) {
  const [selectedState, setSelectedState] = useState<string>("");
  const [showAllPlans, setShowAllPlans] = useState(false);

  const isMedicaid = parentInsurer.toLowerCase() === "medicaid";
  const isKaiser = parentInsurer.toLowerCase() === "kaiser";
  const isBCBS = parentInsurer.toLowerCase() === "bcbs";
  const isTricare = parentInsurer.toLowerCase() === "tricare";
  
  const regionalPlans = INSURERS_WITH_REGIONAL_PLANS[parentInsurer] || [];
  
  // Get plans for selected state (BCBS/Kaiser can have multiple, Medicaid has one)
  const plansForState = selectedState
    ? regionalPlans.filter(plan => plan.states.includes(selectedState))
    : [];

  // Get Medicaid plan for selected state
  const medicaidPlanForState = selectedState ? MEDICAID_PLANS_BY_STATE[selectedState] : null;

  // For Kaiser in CA, we need to ask NorCal vs SoCal
  const needsSubRegion = isKaiser && selectedState === "CA" && plansForState.length > 1;

  // Dynamic labels
  const insurerDisplayName = isMedicaid 
    ? "Medicaid" 
    : isKaiser 
    ? "Kaiser Permanente" 
    : isTricare
    ? "Tricare"
    : "BCBS";
    
  const headerTitle = isMedicaid 
    ? "Which State Medicaid?" 
    : isKaiser 
    ? "Which Kaiser Region?" 
    : isTricare
    ? "Which Tricare Region?"
    : "Which BCBS Plan?";
    
  const headerSubtitle = isMedicaid
    ? "Each state runs its own Medicaid program with different MCOs and rules."
    : isKaiser
    ? "Kaiser operates as separate regional entities with different formularies."
    : isTricare
    ? "Tricare coverage is managed by different regional contractors."
    : "BCBS plans vary significantly by state. Selecting yours helps us provide better guidance.";

  // Handle state selection for Medicaid (auto-select since one plan per state)
  const handleStateChange = (state: string) => {
    setSelectedState(state);
  };

  const handleMedicaidSelect = () => {
    if (medicaidPlanForState) {
      onSelect(medicaidPlanForState.value, medicaidPlanForState.label);
    }
  };

  // Header gradient color
  const headerGradient = isMedicaid 
    ? "bg-gradient-to-r from-emerald-500 to-teal-500"
    : isKaiser
    ? "bg-gradient-to-r from-blue-500 to-indigo-500"
    : isTricare
    ? "bg-gradient-to-r from-slate-600 to-slate-700"
    : "bg-gradient-to-r from-teal-500 to-cyan-500";

  const headerSubtitleColor = isMedicaid 
    ? "text-emerald-100" 
    : isKaiser 
    ? "text-blue-100" 
    : isTricare
    ? "text-slate-300"
    : "text-teal-100";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className={`px-6 py-4 relative ${headerGradient}`}>
          <button
            onClick={onCancel}
            className="absolute right-4 top-4 p-1.5 rounded-lg hover:bg-white/20 transition-colors"
          >
            <IconX className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <IconMapPin className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {headerTitle}
              </h3>
              <p className={`text-sm mt-0.5 ${headerSubtitleColor}`}>
                {headerSubtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* State selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {isMedicaid 
                ? "What state is the patient's Medicaid from?" 
                : isKaiser
                ? "What state is the patient located in?"
                : "What state is the plan from?"}
            </label>
            <select
              value={selectedState}
              onChange={(e) => handleStateChange(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 text-slate-700 transition-all appearance-none cursor-pointer"
              style={{ 
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, 
                backgroundPosition: 'right 0.5rem center', 
                backgroundRepeat: 'no-repeat', 
                backgroundSize: '1.5em 1.5em', 
                paddingRight: '2.5rem' 
              }}
            >
              <option value="">Select a state...</option>
              {US_STATES.map(state => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
          </div>

          {/* Medicaid: Show plan details for selected state */}
          {isMedicaid && selectedState && medicaidPlanForState && (
            <div className="mb-4">
              <button
                onClick={handleMedicaidSelect}
                className="w-full text-left p-4 rounded-xl border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/50 transition-all group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-semibold text-slate-800 group-hover:text-emerald-700 transition-colors text-lg">
                      {medicaidPlanForState.programName}
                    </div>
                    <div className="text-sm text-slate-600 mt-1">
                      {medicaidPlanForState.label}
                    </div>
                    
                    {medicaidPlanForState.majorMCOs && medicaidPlanForState.majorMCOs.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">
                          Major MCOs
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {medicaidPlanForState.majorMCOs.slice(0, 6).map((mco, idx) => (
                            <span 
                              key={idx}
                              className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md bg-slate-100 text-slate-600"
                            >
                              {mco}
                            </span>
                          ))}
                          {medicaidPlanForState.majorMCOs.length > 6 && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md bg-slate-100 text-slate-500">
                              +{medicaidPlanForState.majorMCOs.length - 6} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {medicaidPlanForState.notes && (
                      <div className="mt-2 text-xs text-slate-500 italic">
                        {medicaidPlanForState.notes}
                      </div>
                    )}
                  </div>
                  <div className="ml-3 mt-1">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 group-hover:bg-emerald-200 flex items-center justify-center transition-colors">
                      <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Medicaid: State not found */}
          {isMedicaid && selectedState && !medicaidPlanForState && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200/60 rounded-xl">
              <p className="text-sm text-amber-800">
                We don't have specific data for Medicaid in <span className="font-medium">{US_STATES.find(s => s.value === selectedState)?.label}</span>. 
                You can continue with generic Medicaid.
              </p>
            </div>
          )}

          {/* BCBS/Kaiser/Tricare: Plans for selected state */}
          {!isMedicaid && selectedState && plansForState.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {plansForState.length === 1 ? "Your region:" : "Select your region:"}
              </label>
              <div className="space-y-2">
                {plansForState.map(plan => (
                  <button
                    key={plan.value}
                    onClick={() => onSelect(plan.value, plan.label)}
                    className="w-full text-left px-4 py-3 rounded-xl border-2 border-slate-200 hover:border-teal-500 hover:bg-teal-50/50 transition-all group"
                  >
                    <div className="font-medium text-slate-800 group-hover:text-teal-700 transition-colors">
                      {plan.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* BCBS/Kaiser/Tricare: No plans found for state */}
          {!isMedicaid && selectedState && plansForState.length === 0 && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200/60 rounded-xl">
              <p className="text-sm text-amber-800">
                {isKaiser 
                  ? <>Kaiser Permanente doesn't operate in <span className="font-medium">{US_STATES.find(s => s.value === selectedState)?.label}</span>. You may want to select a different insurer.</>
                  : <>We don't have specific data for {insurerDisplayName} in <span className="font-medium">{US_STATES.find(s => s.value === selectedState)?.label}</span>. You can select from all plans below or continue with generic {insurerDisplayName}.</>
                }
              </p>
            </div>
          )}

          {/* Tricare: Show all regions (simple - only 3 options) */}
          {isTricare && !selectedState && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Or select your region directly:
              </label>
              <div className="space-y-2">
                {regionalPlans.map(plan => (
                  <button
                    key={plan.value}
                    onClick={() => onSelect(plan.value, plan.label)}
                    className="w-full text-left px-4 py-3 rounded-xl border-2 border-slate-200 hover:border-slate-500 hover:bg-slate-50 transition-all group"
                  >
                    <div className="font-medium text-slate-800 group-hover:text-slate-900 transition-colors">
                      {plan.label}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {(plan as unknown as TricareRegion).contractor}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* BCBS: Show all plans toggle (not for Kaiser/Tricare - limited regions) */}
          {isBCBS && !selectedState && (
            <button
              onClick={() => setShowAllPlans(!showAllPlans)}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium mb-4 flex items-center gap-1"
            >
              <IconChevronDown className={`w-4 h-4 transition-transform ${showAllPlans ? 'rotate-180' : ''}`} />
              {showAllPlans ? "Hide all plans" : "Show all BCBS plans"}
            </button>
          )}

          {/* Kaiser: Show all regions when no state selected */}
          {isKaiser && !selectedState && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Or select your region directly:
              </label>
              <div className="space-y-2">
                {regionalPlans.map(plan => (
                  <button
                    key={plan.value}
                    onClick={() => onSelect(plan.value, plan.label)}
                    className="w-full text-left px-4 py-3 rounded-xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50/50 transition-all group"
                  >
                    <div className="font-medium text-slate-800 group-hover:text-blue-700 transition-colors">
                      {plan.label}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {plan.states.join(", ")}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* BCBS: All plans list */}
          {isBCBS && (showAllPlans || (selectedState && plansForState.length === 0)) && (
            <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-xl">
              {regionalPlans.map((plan, index) => (
                <button
                  key={plan.value}
                  onClick={() => onSelect(plan.value, plan.label)}
                  className={`w-full text-left px-4 py-2.5 hover:bg-teal-50/50 transition-colors ${
                    index !== regionalPlans.length - 1 ? 'border-b border-slate-100' : ''
                  }`}
                >
                  <div className="text-sm font-medium text-slate-700">{plan.label}</div>
                  <div className="text-xs text-slate-400">
                    {plan.states.join(", ")}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium rounded-lg hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onSelect(
              parentInsurer.toLowerCase(), 
              `${insurerDisplayName} (General)`
            )}
            className="px-4 py-2 text-slate-500 hover:text-slate-700 text-sm hover:bg-slate-100 rounded-lg transition-colors"
          >
            Continue with generic {insurerDisplayName}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Check if an insurer selection should trigger the regional plan selector
 */
export function shouldShowRegionalSelector(insurerValue: string): boolean {
  return REGIONAL_INSURERS.includes(insurerValue.toLowerCase());
}

/**
 * Get the criteria system for a specific BCBS plan
 */
export function getPlanCriteriaSystem(planValue: string): string | null {
  for (const plans of Object.values(INSURERS_WITH_REGIONAL_PLANS)) {
    const plan = plans.find(p => p.value === planValue);
    if (plan?.criteriaSystem) {
      return plan.criteriaSystem;
    }
  }
  return null;
}

/**
 * Get display label for a regional plan value (BCBS or Medicaid)
 */
export function getRegionalPlanLabel(planValue: string): string | null {
  // Check BCBS plans
  for (const plans of Object.values(INSURERS_WITH_REGIONAL_PLANS)) {
    const plan = plans.find(p => p.value === planValue);
    if (plan) {
      return plan.label;
    }
  }
  
  // Check Medicaid plans
  const medicaidPlan = MEDICAID_STATE_PLANS.find(p => p.value === planValue);
  if (medicaidPlan) {
    return medicaidPlan.label;
  }
  
  return null;
}

/**
 * Get Medicaid plan details for a given plan value
 */
export function getMedicaidPlanDetails(planValue: string): MedicaidPlan | null {
  return MEDICAID_STATE_PLANS.find(p => p.value === planValue) || null;
}

/**
 * Get all MCOs for a Medicaid plan
 */
export function getMedicaidMCOs(planValue: string): string[] {
  const plan = MEDICAID_STATE_PLANS.find(p => p.value === planValue);
  return plan?.majorMCOs || [];
}

export default RegionalPlanSelector;